import React, { useState } from "react";

import AutoCompleteInput from "common/autoComplete/components/AutoCompleteInput";
import regexp from "common/Regexp";
import { UsersAPI } from "api/users";
import { Icon } from "components/icon/Icon";
import { captureEvent } from "components/posthog/customEventCapture";
import { ExternalUser } from "models/customers/customers";
import { useGlobalContext } from "app/store/GlobalContext";
import { useRootStore } from "store";
import { Loader } from "components/loader/Loader";

import "./AddExternalUsers.scss";

type Props = {
  selectedUsers: ExternalUser[];
  itemAdded: (user: Partial<ExternalUser>, isNew?:boolean) => void;
};

type Validation = {
  givenName: string;
  surName: string;
  socialSecurityNumber: string;
  email: string;
};

export default function AddExternalUsers({ selectedUsers, itemAdded }: Props) {
  const { setEditingState } = useGlobalContext();
  const { workingCustomerId } = useRootStore((state) => state.global);
  const { appendErrorNotification } = useRootStore((state) => state.error);

  const [isLoadingNewUser, setIsLoadingNewUser] = useState(false);

  const emptyUser = () => {
    return {
      id: undefined,
      givenName: "",
      surName: "",
      socialSecurityNumber: "",
      email: "",
      role: "user"
    };
  };

  const [currentUser, setCurrentUser] =
    useState<Partial<ExternalUser>>(emptyUser());
  const [validation, setValidation] = useState<Validation>({
    givenName: "",
    surName: "",
    socialSecurityNumber: "",
    email: ""
  });
  const [autocompleteUsers, setAutocompleteUsers] = useState<
    Partial<ExternalUser>[]
  >([]);
  const [options, setOptions] = useState<(string | undefined)[][]>([]);
  const [isInternal, setIsInternal] = useState(false);

  const handleUserSelected = (index: number) => {
    const autoCompleteUser = autocompleteUsers[index];
    autoCompleteUser.roles = [];

    const validate = { ...validation };
    if (autoCompleteUser.email && autoCompleteUser.email.length < 1) {
      validate.email = "EMPTY";
    } else {
      validateRegexp(regexp.email, autoCompleteUser.email)
        ? (validate.email = "VALID")
        : (validate.email = "INVALID");
    }

    setCurrentUser(autoCompleteUser);
    setValidation(validate);
    setOptions([]);
    setAutocompleteUsers([]);
  };

  const validateRegexp = (regex: RegExp, value?: string) => {
    if (value && value.length < 1) {
      return "EMPTY";
    }
    return value && value.match(regex) ? "VALID" : "INVALID";
  };

  const validateInputs = (validateUser?: Partial<ExternalUser>) => {
    const user = validateUser ? validateUser : { ...currentUser };
    const validate = { ...validation };

    validate.socialSecurityNumber = validateRegexp(
      regexp.ssn,
      user.socialSecurityNumber
    );
    validate.givenName = validateRegexp(regexp.name, user.givenName);
    validate.surName = validateRegexp(regexp.name, user.surName);
    return validate;
  };

  const parseUserSearchResult = (users: Partial<ExternalUser>[]) => {
    return filterAlreadySelectedUsers(users).map((user) => {
      return [user.givenName, user.email];
    });
  };

  const filterAlreadySelectedUsers = (users: Partial<ExternalUser>[]) => {
    return users.filter((user) => {
      return !selectedUsers.some((sel) => sel.email === user.email);
    });
  };

  const searchUsers = (query: string) => {
    if (query.length < 3) {
      setOptions([]);
      setAutocompleteUsers([]);
      return;
    }

    UsersAPI.searchExternalUsers(query)
      .then((result) => {
        setAutocompleteUsers(
          filterAlreadySelectedUsers(JSON.parse(result.text).items)
        );
        setOptions(parseUserSearchResult(JSON.parse(result.text).items));
      })
      .catch((error) => {
        if (!error.response) {
          throw new Error(error);
        }
        switch (error.response.status) {
          case 404:
            setOptions([]);
            break;
          default:
            throw new Error(error);
        }
      });
  };

  const resetForm = (value: string) => {
    const user = {
      id: undefined,
      givenName: "",
      surName: "",
      socialSecurityNumber: "",
      email: "",
      role: "user"
    };
    setCurrentUser(user);
    setValidation({
      givenName: "",
      surName: "",
      socialSecurityNumber: "",
      email: ""
    });
    validateInputs();
    searchUsers(value);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingState({ isEditing: true, location: "customer-settings" });
    const value = e.target.value;
    if (value.length === 0) {
      resetForm(value);
      return;
    }

    let user = { ...currentUser };
    if (!user.id) {
      user = {
        id: undefined,
        givenName: "",
        surName: "",
        socialSecurityNumber: "",
        email: currentUser.email,
        roles: []
      };
    }
    user.email = value;
    const validate = { ...validation };
    value.match(regexp.email)
      ? (validate.email = "VALID")
      : (validate.email = "INVALID");

    if (value.length < 1) {
      validate.email = "EMPTY";
    }
    setCurrentUser(user);
    setValidation(validate);
    validateInputs();
    searchUsers(value);

    const notAlowed = [
      "ludvig.se",
      "ludvig.nu",
      "lrfkonsult.se",
      "ludvigfast.se",
      "bokoredo.com",
      "bokoredo.se",
      "lrfk.se"
    ];
    setIsInternal(false);

    notAlowed.forEach((element) => {
      const test = value.toLowerCase();
      if (test.includes(element)) {
        setIsInternal(true);
      }
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingState({ isEditing: true, location: "customer-settings" });

    const { value, name } = e.target;
    const user = { ...currentUser, [name]: value.trim() };
    setCurrentUser(user);
    const validate = validateInputs(user);
    setValidation(validate);
  };

  const confirmValidation = () => {
    const validate = validateInputs();

    return !Object.values(validate).some((status) => status === "INVALID");
  };

  const handleItemConfirm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (confirmValidation()) {
      const userToAdd = { ...currentUser };
      const userExists = !!userToAdd.id;

      if (userExists) {
        if (userToAdd.givenName && userToAdd.surName) {
          userToAdd.givenName = userToAdd.givenName.trim();
          userToAdd.surName = userToAdd.surName.trim();
          itemAdded({ ...userToAdd });
        }
      } else {
        try {
          setIsLoadingNewUser(true);
          const newUser = {
            givenName: userToAdd.givenName,
            surName: userToAdd.surName,
            email: userToAdd.email,
            socialSecurityNumber: userToAdd.socialSecurityNumber
          };
          const user = await UsersAPI.addNewExternalUserToCustomer(
            workingCustomerId,
            newUser
          );
          itemAdded(user, true);
        } catch (error) {
          appendErrorNotification(
            "Misslyckades med att lägga till användare",
            error
          );
        } finally {
          setIsLoadingNewUser(false);
        }
      }

      setCurrentUser(emptyUser());
      setValidation({
        givenName: "EMPTY",
        surName: "EMPTY",
        socialSecurityNumber: "EMPTY",
        email: "EMPTY"
      });
    }
  };

  return (
    <form onSubmit={(e) => handleItemConfirm(e)}>
      <div className="addCustomerUsers">
        <fieldset>
          {isInternal ? (
            <div className="warning">
              <Icon name={"warning"}></Icon> Här kan du bara lägga till
              kundanvändare
            </div>
          ) : null}
        </fieldset>
        <fieldset>
          <div>
            <label>E-post</label>
            <AutoCompleteInput
              className={
                isInternal ? "invalid" : validation.email.toLowerCase()
              }
              selectKey={1}
              options={options}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleOnChange(e)
              }
              onUserSelected={handleUserSelected}
              placeholder="Ange e-post"
              value={currentUser.email}
              disabled={isLoadingNewUser}
            />
          </div>
          <div>
            <label>Personnummer</label>
            <input
              id="personalNumberInputField"
              placeholder="ÅÅÅÅMMDDXXXX"
              className={validation.socialSecurityNumber.toLowerCase()}
              disabled={
                !!currentUser.id ||
                validation.email !== "VALID" ||
                isLoadingNewUser
              }
              value={currentUser.socialSecurityNumber}
              onChange={(e) => handleInputChange(e)}
              onBlur={(e) => setValidation(validateInputs())}
              type="text"
              name="socialSecurityNumber"
            />
          </div>
          <div>
            <label>Förnamn</label>
            <input
              id="prenameInputField"
              className={validation.givenName.toLowerCase()}
              disabled={
                !!currentUser.id ||
                validation.email !== "VALID" ||
                isLoadingNewUser
              }
              value={currentUser.givenName}
              onChange={(e) => handleInputChange(e)}
              onBlur={() => setValidation(validateInputs())}
              type="text"
              name="givenName"
            />
          </div>
          <div>
            <label>Efternamn</label>
            <input
              id="surnameInputField"
              className={validation.surName.toLocaleLowerCase()}
              disabled={
                !!currentUser.id ||
                validation.email !== "VALID" ||
                isLoadingNewUser
              }
              value={currentUser.surName}
              onChange={(e) => handleInputChange(e)}
              onBlur={() => setValidation(validateInputs())}
              type="text"
              name="surName"
            />
          </div>
        </fieldset>

        <fieldset>
          <button
            className="button"
            id="addCustomerUserButton"
            type="submit"
            disabled={!confirmValidation() || isLoadingNewUser}
            onClick={() => captureEvent("Lägg till användare")}
          >
            <div className="d-flex align-items-center">
              <span>Lägg till användare</span>
              {isLoadingNewUser && <Loader className="ml-3" />}
            </div>
          </button>
        </fieldset>
      </div>
    </form>
  );
}
